import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Stack, Typography, Chip, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-material.css';
import '../styles/ag-grid-custom.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar, faRefresh, faArrowLeft, faTrophy, faEye, faX } from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/apiService';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface PlayerScore {
  _id: string;
  playerId: number;
  seriesId: number;
  playerName: string;
  teamId: number;
  matches: Array<{
    _id: string;
    matchId: number;
    scoresBreakdown: {
      runsScored: number;
      ballsFaced: number;
      fours: number;
      sixes: number;
      strikeRate: number;
      wickets: number;
      overs: number;
      runsConceded: number;
      economy: number;
      catch: number;
      stump: number;
      runouts: number;
      mom: number;
      mos: number;
      // Series-wide awards. Backend writes these onto the player's
      // final-match entry once all series matches are terminal
      // (seriesService.applySeriesCapAwardsIfComplete). Default 0.
      orangeCap?: number;
      purpleCap?: number;
    };
  }>;
}

const PlayerScores: React.FC = () => {
  const { seriesId } = useParams<{ seriesId: string }>();
  const navigate = useNavigate();
  const [playerScores, setPlayerScores] = useState<PlayerScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState<any | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const loadPlayerScores = useCallback(async () => {
    if (!seriesId) return;
    try {
      setLoading(true);
      const data = await apiService.getPlayerScores(seriesId);
      console.log('Player Scores:', data);
      setPlayerScores(data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching player scores:', err);
      setError('Failed to fetch player scores');
    } finally {
      setLoading(false);
    }
  }, [seriesId]);

  useEffect(() => {
    loadPlayerScores();
  }, [loadPlayerScores]);

  const handleViewDetails = useCallback((player: any) => {
    setSelectedPlayer(player);
    setDetailsOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
    setSelectedPlayer(null);
  }, []);

  // Flatten data for AG Grid - one row per player with aggregated stats
  const flattenedData = useMemo(() => {
    return playerScores.map((player) => {
      // Aggregate stats across all matches
      const totalStats = player.matches.reduce(
        (acc, match) => {
          const breakdown = match.scoresBreakdown;
          return {
            matches: acc.matches + 1,
            runsScored: acc.runsScored + breakdown.runsScored,
            ballsFaced: acc.ballsFaced + breakdown.ballsFaced,
            fours: acc.fours + breakdown.fours,
            sixes: acc.sixes + breakdown.sixes,
            wickets: acc.wickets + breakdown.wickets,
            overs: acc.overs + breakdown.overs,
            runsConceded: acc.runsConceded + breakdown.runsConceded,
            catches: acc.catches + breakdown.catch,
            stumps: acc.stumps + breakdown.stump,
            runouts: acc.runouts + breakdown.runouts,
            mom: acc.mom + breakdown.mom,
            mos: acc.mos + breakdown.mos,
            orangeCap: acc.orangeCap + (breakdown.orangeCap || 0),
            purpleCap: acc.purpleCap + (breakdown.purpleCap || 0),
          };
        },
        {
          matches: 0,
          runsScored: 0,
          ballsFaced: 0,
          fours: 0,
          sixes: 0,
          wickets: 0,
          overs: 0,
          runsConceded: 0,
          catches: 0,
          stumps: 0,
          runouts: 0,
          mom: 0,
          mos: 0,
          orangeCap: 0,
          purpleCap: 0,
        }
      );

      // Calculate averages
      const battingAvg = totalStats.ballsFaced > 0 ? (totalStats.runsScored / totalStats.ballsFaced) * 100 : 0;
      const bowlingAvg = totalStats.wickets > 0 ? totalStats.runsConceded / totalStats.wickets : 0;
      const economy = totalStats.overs > 0 ? totalStats.runsConceded / totalStats.overs : 0;

      return {
        ...player,
        ...totalStats,
        battingAvg: battingAvg.toFixed(2),
        bowlingAvg: bowlingAvg > 0 ? bowlingAvg.toFixed(2) : '-',
        economy: economy > 0 ? economy.toFixed(2) : '-',
      };
    });
  }, [playerScores]);

  // Cell Renderers
  const ViewDetailsRenderer = useCallback((params: ICellRendererParams) => {
    const hasMatches = params.data.matches && params.data.matches.length > 0;

    if (!hasMatches) return null;

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FontAwesomeIcon icon={faEye} />}
          onClick={() => handleViewDetails(params.data)}
          sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
        >
          View
        </Button>
      </Box>
    );
  }, [handleViewDetails]);

  const PlayerNameRenderer = useCallback((params: ICellRendererParams) => {
    return (
      <Box>
        <Box sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{params.data.playerName}</Box>
        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
          Player ID: {params.data.playerId}
        </Box>
      </Box>
    );
  }, []);

  const AwardsRenderer = useCallback((params: ICellRendererParams) => {
    const { mom, mos, orangeCap = 0, purpleCap = 0 } = params.data;
    if (mom === 0 && mos === 0 && orangeCap === 0 && purpleCap === 0) {
      return <Typography variant="body2" color="text.secondary">-</Typography>;
    }
    return (
      <Stack direction="row" spacing={0.5}>
        {mom > 0 && (
          <Chip
            label={`${mom} MOM`}
            size="small"
            color="warning"
            icon={<FontAwesomeIcon icon={faTrophy} />}
            sx={{ fontSize: '0.7rem', height: '20px' }}
          />
        )}
        {mos > 0 && (
          <Chip
            label={`${mos} MOS`}
            size="small"
            color="error"
            icon={<FontAwesomeIcon icon={faTrophy} />}
            sx={{ fontSize: '0.7rem', height: '20px' }}
          />
        )}
        {orangeCap > 0 && (
          <Chip
            label="Orange Cap"
            size="small"
            icon={<FontAwesomeIcon icon={faTrophy} />}
            sx={{ fontSize: '0.7rem', height: '20px', bgcolor: '#f59e0b', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
            title="Series-wide award — most runs across the series"
          />
        )}
        {purpleCap > 0 && (
          <Chip
            label="Purple Cap"
            size="small"
            icon={<FontAwesomeIcon icon={faTrophy} />}
            sx={{ fontSize: '0.7rem', height: '20px', bgcolor: '#7c3aed', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
            title="Series-wide award — most wickets across the series"
          />
        )}
      </Stack>
    );
  }, []);

  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'details',
        headerName: 'Details',
        width: 100,
        cellRenderer: ViewDetailsRenderer,
        sortable: false,
        filter: false,
        pinned: 'left',
      },
      {
        field: 'playerName',
        headerName: 'Player',
        flex: 1,
        minWidth: 200,
        cellRenderer: PlayerNameRenderer,
        autoHeight: true,
        filter: 'agTextColumnFilter',
        pinned: 'left',
      },
      {
        field: 'teamId',
        headerName: 'Team',
        width: 100,
        filter: 'agNumberColumnFilter',
      },
      // Batting Stats
      {
        field: 'matches',
        headerName: 'Matches',
        width: 90,
        filter: 'agNumberColumnFilter',
        headerClass: 'ag-header-batting',
      },
      {
        field: 'runsScored',
        headerName: 'Runs',
        width: 100,
        filter: 'agNumberColumnFilter',
        cellStyle: { fontWeight: 600 },
        headerClass: 'ag-header-batting',
      },
      {
        field: 'ballsFaced',
        headerName: 'Balls',
        width: 100,
        filter: 'agNumberColumnFilter',
        headerClass: 'ag-header-batting',
      },
      {
        field: 'battingAvg',
        headerName: 'Strike Rate',
        width: 110,
        filter: 'agNumberColumnFilter',
        headerClass: 'ag-header-batting',
      },
      {
        field: 'fours',
        headerName: '4s',
        width: 80,
        filter: 'agNumberColumnFilter',
        headerClass: 'ag-header-batting',
      },
      {
        field: 'sixes',
        headerName: '6s',
        width: 80,
        filter: 'agNumberColumnFilter',
        headerClass: 'ag-header-batting',
      },
      // Bowling Stats
      {
        field: 'wickets',
        headerName: 'Wickets',
        width: 100,
        filter: 'agNumberColumnFilter',
        cellStyle: { fontWeight: 600 },
        headerClass: 'ag-header-bowling',
      },
      {
        field: 'overs',
        headerName: 'Overs',
        width: 100,
        filter: 'agNumberColumnFilter',
        headerClass: 'ag-header-bowling',
      },
      {
        field: 'runsConceded',
        headerName: 'Runs Conc.',
        width: 110,
        filter: 'agNumberColumnFilter',
        headerClass: 'ag-header-bowling',
      },
      {
        field: 'economy',
        headerName: 'Economy',
        width: 100,
        filter: 'agNumberColumnFilter',
        headerClass: 'ag-header-bowling',
      },
      {
        field: 'bowlingAvg',
        headerName: 'Bowling Avg',
        width: 110,
        filter: 'agNumberColumnFilter',
        headerClass: 'ag-header-bowling',
      },
      // Fielding Stats
      {
        field: 'catches',
        headerName: 'Catches',
        width: 100,
        filter: 'agNumberColumnFilter',
        headerClass: 'ag-header-fielding',
      },
      {
        field: 'stumps',
        headerName: 'Stumps',
        width: 100,
        filter: 'agNumberColumnFilter',
        headerClass: 'ag-header-fielding',
      },
      {
        field: 'runouts',
        headerName: 'Runouts',
        width: 100,
        filter: 'agNumberColumnFilter',
        headerClass: 'ag-header-fielding',
      },
      // Awards
      {
        field: 'awards',
        headerName: 'Awards',
        width: 180,
        cellRenderer: AwardsRenderer,
        autoHeight: true,
        filter: false,
      },
    ],
    [ViewDetailsRenderer, PlayerNameRenderer, AwardsRenderer]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    []
  );

  return (
    <Box sx={{ p: { xs: 3, lg: 6 } }}>
      <Box sx={{ width: '100%', height: 'calc(100vh - 150px)' }}>
        {/* Header */}
        <Box
        sx={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 50%, #c084fc 100%)',
          p: 3,
          mb: 0,
          borderRadius: '8px 8px 0 0',
          color: 'white',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Stack direction="row" alignItems="center" spacing={2}>
              <FontAwesomeIcon icon={faChartBar} size="2x" />
              <Box>
                <Box sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Player Scores</Box>
                <Box sx={{ fontSize: '0.875rem', opacity: 0.9 }}>
                  Series ID: {seriesId} • {flattenedData.length} players
                </Box>
              </Box>
            </Stack>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              size="small"
              startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              onClick={() => navigate('/dashboard')}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                color: 'white',
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<FontAwesomeIcon icon={faRefresh} />}
              onClick={() => loadPlayerScores()}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                color: 'white',
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Error Message */}
      {error && (
        <Box sx={{ px: 3, py: 2, bgcolor: '#fef2f2', borderLeft: '4px solid #ef4444' }}>
          <Box sx={{ color: '#991b1b', fontSize: '0.875rem', fontWeight: 500 }}>{error}</Box>
        </Box>
      )}

      {/* AG Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">Loading player scores...</Typography>
        </Box>
      ) : (
        <Box className="ag-theme-material" sx={{ height: 'calc(100% - 150px)', width: '100%' }}>
          <AgGridReact
            rowData={flattenedData}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={25}
            paginationPageSizeSelector={[10, 25, 50, 100]}
            rowHeight={60}
            animateRows={true}
            domLayout="normal"
            theme={themeQuartz}
          />
        </Box>
      )}
      </Box>

      {/* Match Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            sx: { minHeight: '70vh' }
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 50%, #c084fc 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedPlayer?.playerName} - Match-by-Match Performance
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {selectedPlayer?.matches?.length || 0} matches • Player ID: {selectedPlayer?.playerId}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDetails} sx={{ color: 'white' }}>
            <FontAwesomeIcon icon={faX} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedPlayer && selectedPlayer.matches && selectedPlayer.matches.length > 0 && (
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#e3f2fd', borderBottom: '2px solid #90caf9' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Match ID</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Runs</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Balls</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>SR</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>4s</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>6s</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Wkts</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Overs</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Runs Conc.</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Econ</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Catches</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Stumps</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Runouts</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Awards</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPlayer.matches.map((match: any, index: number) => (
                    <tr
                      key={match._id}
                      style={{
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                      }}
                    >
                      <td style={{ padding: '12px', fontWeight: 600 }}>{match.matchId}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{match.scoresBreakdown.runsScored}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{match.scoresBreakdown.ballsFaced}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {match.scoresBreakdown.strikeRate ? match.scoresBreakdown.strikeRate.toFixed(2) : '-'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{match.scoresBreakdown.fours}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{match.scoresBreakdown.sixes}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{match.scoresBreakdown.wickets}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{match.scoresBreakdown.overs}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{match.scoresBreakdown.runsConceded}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {match.scoresBreakdown.economy ? match.scoresBreakdown.economy.toFixed(2) : '-'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{match.scoresBreakdown.catch}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{match.scoresBreakdown.stump}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{match.scoresBreakdown.runouts}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {match.scoresBreakdown.mom > 0 && (
                            <Chip label="MOM" size="small" color="warning" sx={{ fontSize: '0.65rem', height: '18px' }} />
                          )}
                          {match.scoresBreakdown.mos > 0 && (
                            <Chip label="MOS" size="small" color="error" sx={{ fontSize: '0.65rem', height: '18px' }} title="Series-wide award" />
                          )}
                          {(match.scoresBreakdown.orangeCap || 0) > 0 && (
                            <Chip label="OC" size="small" sx={{ fontSize: '0.65rem', height: '18px', bgcolor: '#f59e0b', color: '#fff' }} title="Orange Cap — series-wide award" />
                          )}
                          {(match.scoresBreakdown.purpleCap || 0) > 0 && (
                            <Chip label="PC" size="small" sx={{ fontSize: '0.65rem', height: '18px', bgcolor: '#7c3aed', color: '#fff' }} title="Purple Cap — series-wide award" />
                          )}
                          {match.scoresBreakdown.mom === 0 && match.scoresBreakdown.mos === 0 && !match.scoresBreakdown.orangeCap && !match.scoresBreakdown.purpleCap && '-'}
                        </Stack>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PlayerScores;
